/**
 * Login Page - Entry point for user authentication
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, login, checkAuth } = useAuth();
  // Set by AuthCallback when the backend refuses to issue a session at all
  // (e.g. 403 BETA_EXPIRED). Without it an expired account is bounced back
  // here with no explanation.
  const authError = location.state?.authError;
  const authErrorMessage = location.state?.authErrorMessage;

  // Password sign-in, alongside Google rather than instead of it. The
  // server returns ONE generic refusal for every failure reason (unknown
  // address, wrong password, locked out) - this page displays what it is
  // given and never tries to interpret which happened, because doing so
  // would reconstruct the enumeration oracle the endpoint avoids.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);
    setSubmitting(true);
    try {
      await api.passwordLogin(email, password);
      await checkAuth();
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLoginError(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Sign in failed. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (!loading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleDevLogin = async () => {
    try {
      await api.devLogin();
      await checkAuth();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Dev login failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055FF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="bg-[#0a1628] rounded-2xl p-6 mb-6 mx-auto" style={{maxWidth: '380px'}}>
            <img 
              src="/sahinta-logo.png" 
              alt="Sahinta - The Wisdom" 
              className="w-full h-auto"
            />
          </div>
          <img src="/brand/logo-full.svg" alt="ProcMatrix" className="h-16 mx-auto mb-2" />
          <p className="text-zinc-600">AI-Powered Procurement Platform</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-8">
          <h2 className="text-xl font-semibold text-zinc-900 mb-2 text-center">
            Welcome back
          </h2>
          <p className="text-zinc-500 text-sm text-center mb-6">
            Sign in to manage your procurement requests
          </p>

          {authError && (
            <div
              className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              role="alert"
              data-testid="auth-error-banner"
            >
              {authErrorMessage || 'Sign in was refused. Please contact ashokr@sahinta.com.'}
            </div>
          )}

          <Button
            onClick={login}
            className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white py-6 text-base"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-zinc-200" />
            <span className="text-xs uppercase tracking-wide text-zinc-400">or</span>
            <span className="h-px flex-1 bg-zinc-200" />
          </div>

          {loginError && (
            <div
              className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              role="alert"
              data-testid="password-login-error"
            >
              {loginError}
            </div>
          )}

          <form onSubmit={handlePasswordLogin} data-testid="password-login-form">
            <label className="block text-xs font-medium text-zinc-600 mb-1" htmlFor="login-email">
              Email
            </label>
            <Input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              className="mb-3"
              data-testid="login-email"
            />
            <label className="block text-xs font-medium text-zinc-600 mb-1" htmlFor="login-password">
              Password
            </label>
            <Input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="login-password"
            />
            <Button
              type="submit"
              variant="outline"
              disabled={submitting}
              className="w-full mt-4 py-6 text-base"
              data-testid="password-login-submit"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign in with password'}
            </Button>
          </form>

          {/*
            Self-service guidance, PLAIN TEXT BY DESIGN. There is no "forgot
            password" control and no form that submits an address, because a
            self-serve reset would have to answer "does this account exist?"
            - which is precisely the account-enumeration surface /auth/login
            gives up its helpfulness to avoid (one generic refusal for every
            failure reason). Invites and resets are admin-initiated by
            design; see the invite and reset commits on this branch. The
            mailto: below opens the visitor's own mail client and touches no
            API.

            The address and phrasing deliberately match the BETA_EXPIRED
            refusal the same visitor may already have seen ("To request an
            extension, please contact ashokr@sahinta.com").
          */}
          <div
            className="text-xs text-zinc-400 text-center mt-6 space-y-2"
            data-testid="contact-admin-note"
          >
            <p>
              If you don&apos;t have a Google account, you can request an invite by writing to{' '}
              <a href="mailto:ashokr@sahinta.com" className="underline hover:text-zinc-600">
                ashokr@sahinta.com
              </a>
              . Accounts are created by administrator invitation &mdash; there is no self-signup.
            </p>
            <p>
              To request a password reset or a beta usage-cap reset, please contact{' '}
              <a href="mailto:ashokr@sahinta.com" className="underline hover:text-zinc-600">
                ashokr@sahinta.com
              </a>
              .
            </p>
          </div>

          {process.env.REACT_APP_DEV_AUTH === 'true' && (
            <Button
              onClick={handleDevLogin}
              variant="outline"
              className="w-full mt-3 border-dashed border-amber-400 text-amber-700 hover:bg-amber-50"
              data-testid="dev-login-btn"
            >
              Dev Login (local bypass)
            </Button>
          )}

          <p className="text-xs text-zinc-400 text-center mt-6">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-zinc-400 text-sm mt-6">
          Secure authentication powered by Google OAuth
        </p>
      </div>
    </div>
  );
};

export default Login;
